import { useEffect, useRef, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { formElementTypes } from "../../../../../utils/Constant";
import TableCellField from "../html-form/table-cell-fields";
import TableSelectField from "../html-form/table-select-field";

export default function QuestionTable({
  element,
  inputValueChange,
  disabled = false,
  isQC = false,
}: any) {
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [sum, setSum] = useState<any[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (element && element?.type === formElementTypes.table) {
      tableChildElements();

      const sumColumn = element?.childElements.filter(
        (child: any) =>
          child.tableElementSettings?.headerSettings?.calculateSum,
      );
      const allSums: any[] = [];
      sumColumn.forEach((column: any) => {
        const rows = element?.childElements.filter(
          (child: any) =>
            child.tableElementSettings?.column ===
              column.tableElementSettings?.column &&
            child.tableElementSettings?.row > 0,
        );
        let sum = 0;
        if (isQC) {
          rows.forEach((row: any) => {
            if (
              row?.answers?.qc?.answer &&
              !isNaN(Number(row?.answers?.qc?.answer))
            ) {
              sum += Number(row?.answers?.qc?.answer);
            } else if (
              row?.answers?.inspector?.answer &&
              !isNaN(Number(row?.answers?.inspector?.answer))
            ) {
              sum += Number(row?.answers?.inspector?.answer);
            }
          });
        } else {
          rows.forEach((row: any) => {
            if (
              row?.answers?.inspector?.answer &&
              !isNaN(Number(row?.answers?.inspector?.answer))
            ) {
              sum += Number(row?.answers?.inspector?.answer);
            }
          });
        }
        const totalSum = {
          sum,
          field: `col${column.tableElementSettings?.column + 1}`,
        };
        allSums.push(totalSum);
      });
      setSum(allSums);
    }
  }, [element, tableRef?.current?.getBoundingClientRect().width]); // eslint-disable-line

  useEffect(() => {
    if (sum.length > 0) {
      const sumObj: any = {};
      sum.forEach((s) => {
        sumObj[s.field] = s.sum;
      });
      setRows((prev) =>
        prev.map((row) => (row.id === "sum" ? { ...row, ...sumObj } : row)),
      );
    }
  }, [sum]); // eslint-disable-line

  const tableChildElements = () => {
    const filterHeader = element?.childElements
      .filter((element: any) => element?.tableElementSettings?.isHeader)
      .sort(
        (a: any, b: any) =>
          a?.tableElementSettings?.column - b?.tableElementSettings?.column,
      );
    const filterNonHeader = element?.childElements.filter(
      (element: any) => element?.tableElementSettings?.isHeader === false,
    );

    const filterAutoWidthLength = filterHeader.filter(
      (col: any) => col.tableElementSettings.headerSettings.width === 0,
    ).length;
    const filterNonAutoWidth = filterHeader.filter(
      (col: any) => col.tableElementSettings.headerSettings.width !== 0,
    );
    let nonAutoWidthSum = 0;
    filterNonAutoWidth.forEach((col: any) => {
      nonAutoWidthSum += col.tableElementSettings.headerSettings.width;
    });
    const tableWidth = tableRef?.current?.getBoundingClientRect().width;

    const columns: GridColDef[] = [];
    filterHeader.forEach((header: any, index: number) => {
      let width;
      if (tableWidth) {
        if (header?.tableElementSettings?.headerSettings?.width === 0) {
          width =
            (12 / filterAutoWidthLength / 12) *
            (tableWidth - (nonAutoWidthSum / 12) * tableWidth);
        } else {
          width =
            (header?.tableElementSettings?.headerSettings?.width / 12) *
            tableWidth;
        }
      }
      if (header?.answerOptions?.length > 0) {
        const column: GridColDef & { settings: any } = {
          field: `col${index + 1}`,
          headerName: header?.title,
          width: width,
          editable: true,
          type: "singleSelect",
          valueOptions: ["", ...header?.answerOptions],
          renderHeader: (params) => (
            <Tooltip title={params.colDef.headerName}>
              <Typography
                sx={{
                  width: params.colDef.width,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
                noWrap
              >
                {params.colDef.headerName}
              </Typography>
            </Tooltip>
          ),
          renderEditCell: (params) => (
            <TableSelectField
              header={header}
              cell={filterNonHeader.find(
                (cell: any) =>
                  cell?.tableElementSettings?.column ===
                    header?.tableElementSettings?.column &&
                  cell?.tableElementSettings?.row === params.id,
              )}
              updatedValue={updatedValue}
              disabled={disabled}
              isQC={isQC}
            />
          ),
          renderCell: (params) => {
            if (params.id !== "sum") {
              return (
                <TableSelectField
                  header={header}
                  cell={filterNonHeader.find(
                    (cell: any) =>
                      cell?.tableElementSettings?.column ===
                        header?.tableElementSettings?.column &&
                      cell?.tableElementSettings?.row === params.id,
                  )}
                  updatedValue={updatedValue}
                  disabled={disabled}
                  isQC={isQC}
                />
              );
            }
          },
          settings: {
            label: header?.title,
            inputType: header?.tableElementSettings?.headerSettings?.inputType,
            inputFormat:
              header?.tableElementSettings?.headerSettings?.inputFormat,
            column: header?.tableElementSettings?.column,
            row: header?.tableElementSettings?.row,
            isSum: header?.tableElementSettings?.headerSettings?.calculateSum,
            answerOptions: header?.answerOptions
              ? header?.answerOptions.join(", ")
              : "",
            width: header?.tableElementSettings?.headerSettings?.width,
          },
        };
        columns.push(column);
      } else {
        const column: GridColDef & { settings: any } = {
          field: `col${index + 1}`,
          headerName: header?.title,
          width: width,
          editable: false,
          type: "string",
          renderHeader: (params) => (
            <Tooltip title={params.colDef.headerName}>
              <Typography
                sx={{
                  width: params.colDef.width,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
                noWrap
              >
                {params.colDef.headerName}
              </Typography>
            </Tooltip>
          ),
          renderEditCell: (params) => {
            if (
              header?.tableElementSettings?.headerSettings?.inputType ===
              "OpenEnd"
            ) {
              return <TableCellField header={header} />;
            }
          },
          renderCell: (params) => {
            if (!params.id.toString().includes("sum")) {
              if (
                header?.tableElementSettings?.headerSettings?.inputType ===
                "OpenEnd"
              ) {
                return (
                  <TableCellField
                    header={header}
                    cell={filterNonHeader.find(
                      (cell: any) =>
                        cell?.tableElementSettings?.column ===
                          header?.tableElementSettings?.column &&
                        cell?.tableElementSettings?.row === params.id,
                    )}
                    updatedValue={updatedValue}
                    disabled={disabled}
                    isQC={isQC}
                  />
                );
              }
            } else {
              if (params?.row?.fields?.includes(params.field)) {
                return (
                  <Typography
                    sx={{ display: "flex", justifyContent: "center", my: 1.5 }}
                    color="textDisabled"
                  >
                    Sum={" "}
                    <Typography sx={{ color: "rgba(0, 0, 0, 0.87)", ml: 1 }}>
                      {params?.row[params.colDef.field] || 0}
                    </Typography>
                  </Typography>
                );
              }
            }
          },
          settings: {
            label: header?.title,
            inputType: header?.tableElementSettings?.headerSettings?.inputType,
            inputFormat:
              header?.tableElementSettings?.headerSettings?.inputFormat,
            column: header?.tableElementSettings?.column,
            row: header?.tableElementSettings?.row,
            isSum: header?.tableElementSettings?.headerSettings?.calculateSum,
            answerOptions: header?.answerOptions
              ? header?.answerOptions.join(", ")
              : "",
            width: header?.tableElementSettings?.headerSettings?.width,
          },
        };
        columns.push(column);
      }
    });
    setColumns(columns);

    const rows: any[] = [];
    const numberOfRows = filterNonHeader.length / filterHeader.length;
    for (let i = 1; i <= numberOfRows; i++) {
      const filterRow = element?.childElements
        .filter((element: any) => element?.tableElementSettings?.row === i)
        .sort(
          (a: any, b: any) =>
            a?.tableElementSettings?.column - b?.tableElementSettings?.column,
        );
      const updateRow: any = { id: i };
      filterRow.forEach((row: any, index: number) => {
        const filterColumn: any = columns.find(
          (col) => col.field === `col${index + 1}`,
        );
        if (filterColumn?.settings?.inputType === "TextField") {
          updateRow[`col${index + 1}`] = row?.title;
        }
      });
      rows.push(updateRow);
    }

    const sumColumns = columns.filter((header: any) => header?.settings?.isSum);
    if (sumColumns.length > 0) {
      const fields: string[] = [];
      sumColumns.forEach((col) => {
        fields.push(col.field);
      });
      const sumRow: any = { id: "sum", fields };
      rows.push(sumRow);
    }
    setRows(rows);
  };

  const updatedValue = (value: string, childElementId: string) => {
    if (inputValueChange) {
      inputValueChange({ id: childElementId, answer: value });
    }
  };

  return (
    <DataGrid
      ref={tableRef}
      sx={{
        mt: 1,
        border: "none",
        "& .MuiDataGrid-cell": {
          border: "1px solid rgba(224,224,224,1)",
        },
        "& .MuiDataGrid-columnHeader": {
          border: "1px solid rgba(224,224,224,1)",
        },
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "none",
        },
        "& .MuiDataGrid-cellEmpty": {
          all: "unset",
          display: "none",
        },
        "& .MuiDataGrid-filler": {
          all: "unset",
          display: "none",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "transparent",
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "transparent",
        },
        "& .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "transparent",
        },
        "& .MuiDataGrid-row--borderBottom": {
          backgroundColor: "transparent",
        },
        "& .MuiDataGrid-scrollbarFiller": {
          display: "none",
        },
        "& .MuiDataGrid-scrollbar--horizontal": {
          display: "none",
        },
        "& .MuiDataGrid-scrollbar--vertical": {
          display: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          marginBottom: "0 !important",
        },
      }}
      rows={rows}
      columns={columns}
      isCellEditable={(params) => params.id !== "sum"}
      disableColumnResize
      disableColumnFilter
      disableColumnMenu
      disableColumnSorting
      hideFooter
      slots={{
        noRowsOverlay: () => null,
      }}
      getRowHeight={() => "auto"}
    />
  );
}
